/**
 * Platini Wishlist — localStorage-based favorites
 * Dispatches custom events so any component can react.
 */

(function () {
  const STORAGE_KEY = 'platini_wishlist';

  function normalizeEntry(entry) {
    if (entry == null) return null;
    if (typeof entry === 'string' || typeof entry === 'number') {
      const id = String(entry);
      return id ? { id } : null;
    }
    if (typeof entry === 'object') {
      const id = entry.id != null ? String(entry.id) : '';
      const handle = entry.handle != null ? String(entry.handle) : '';
      if (!id && !handle) return null;
      return { id, handle };
    }
    return null;
  }

  function dedupeWishlist(list) {
    const map = new Map();
    list.forEach((entry) => {
      const normalized = normalizeEntry(entry);
      if (!normalized) return;
      const key = normalized.id || normalized.handle;
      if (!key) return;
      if (!map.has(key)) {
        map.set(key, normalized);
        return;
      }
      const current = map.get(key);
      if (!current.handle && normalized.handle) {
        map.set(key, normalized);
      }
    });
    return Array.from(map.values());
  }

  function getWishlist() {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(parsed)) return [];
      return dedupeWishlist(parsed);
    } catch {
      return [];
    }
  }

  function saveWishlist(list) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  }

  function isWishlisted(productId) {
    const id = String(productId || '');
    if (!id) return false;
    return getWishlist().some((entry) => entry.id === id);
  }

  function toggleWishlist(productId, productHandle) {
    const id = String(productId);
    const handle = productHandle ? String(productHandle) : '';
    let list = getWishlist();
    let added;
    if (list.some((entry) => entry.id === id)) {
      list = list.filter((entry) => entry.id !== id);
      added = false;
    } else {
      list.push({ id, handle });
      list = dedupeWishlist(list);
      added = true;
    }
    saveWishlist(list);
    document.dispatchEvent(
      new CustomEvent('platini:wishlist:change', {
        detail: { productId: id, added, wishlist: list },
      })
    );
    return added;
  }

  // Update all heart buttons to reflect current state
  function syncButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach((btn) => {
      const id = String(btn.dataset.productId);
      const active = isWishlisted(id);
      btn.classList.toggle('is-wishlisted', active);
      btn.setAttribute('aria-label', active ? 'Remove from favourites' : 'Add to favourites');
      btn.setAttribute('aria-pressed', String(active));
    });
  }

  /** Hide “Styles You’ll Love” rec cards for products already saved (no server data). */
  function filterStylesYouLoveCards() {
    document.querySelectorAll('.wl-recs [data-wl-product-id]').forEach((card) => {
      const pid = card.getAttribute('data-wl-product-id');
      if (pid && isWishlisted(pid)) {
        card.remove();
      }
    });
    if (typeof window.wlRecsCarouselUpdate === 'function') {
      window.wlRecsCarouselUpdate();
    }
  }

  // Handle clicks on wishlist buttons
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-btn]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const id = btn.dataset.productId;
    if (!id) return;

    const inRecs = Boolean(btn.closest('.wl-recs'));
    const card = inRecs ? btn.closest('[data-wl-product-id]') : null;

    if (inRecs) {
      const wasListed = isWishlisted(id);
      if (wasListed) {
        toggleWishlist(id, btn.dataset.productHandle || '');
        if (card) card.remove();
        if (typeof window.wlRecsCarouselUpdate === 'function') {
          window.wlRecsCarouselUpdate();
        }
        syncButtons();
        return;
      }
      /* Add to wishlist: short loading state, then update storage + remove this card (no full reload). */
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      const handle = String(btn.dataset.productHandle || '');
      window.setTimeout(() => {
        toggleWishlist(id, handle);
        if (card) card.remove();
        if (btn.isConnected) {
          btn.classList.remove('is-loading');
          btn.removeAttribute('aria-busy');
        }
        if (typeof window.wlRecsCarouselUpdate === 'function') {
          window.wlRecsCarouselUpdate();
        }
        syncButtons();
      }, 200);
      return;
    }

    toggleWishlist(id, btn.dataset.productHandle || '');
    syncButtons();
  });

  // Sync on page load and after any wishlist change
  function initWishlistUI() {
    filterStylesYouLoveCards();
    syncButtons();
  }
  document.addEventListener('DOMContentLoaded', initWishlistUI);
  document.addEventListener('platini:wishlist:change', syncButtons);
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      filterStylesYouLoveCards();
      syncButtons();
    }
  });

  // Expose API
  window.PlatiniWishlist = {
    getWishlist,
    isWishlisted,
    toggleWishlist,
    syncButtons,
    filterStylesYouLoveCards,
  };
})();
